import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizePayload = (values = {}) => ({
  nombre: (values.nombre || '').trim(),
  descripcion: (values.descripcion || '').trim(),
  tipo: values.tipo || 'articulos',
  es_warmer: Boolean(values.es_warmer),
  es_beta: Boolean(values.es_beta),
  es_azucar_glass: Boolean(values.es_azucar_glass),
  activo: Boolean(values.activo)
});

export const useAdminProcesses = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/processes', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los procesos');
      }
      if (!signal?.aborted) {
        setItems(data.processes || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar procesos');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveProcess = useCallback(async (values) => {
    const payload = normalizePayload(values);
    if (!payload.nombre) {
      throw new Error('El nombre del proceso es requerido.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/processes/${values.id}` : '/admin/api/processes';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar el proceso');
    }
    return data;
  }, []);

  const deleteProcess = useCallback(async (id) => {
    const response = await fetch(`/admin/api/processes/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar el proceso');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    loading,
    error,
    refresh,
    saveProcess,
    deleteProcess
  }), [items, loading, error, refresh, saveProcess, deleteProcess]);
};
